const Cookies = require("js-cookie");
const { modeDeploy } = require("../../../codes");
const { legacyWebhookSummaryRequest } = require('./webhookSchemaCompatibility');
const { legacyRecordingPartDetailsRequest } = require('../../../utils/recordingPartDetails');
const { readDashboardListCapabilities, recordingRequestForCapabilities } = require('./dashboardListCapabilities');
const { partDetailsSelection } = require('../../../utils/recordingPartDetails');
const { partialOutcomeSelection } = require('../../../utils/recordingPartialOutcome');
const { STAFF_VERIFICATION_EVENT } = require('./staffSessionClient');
const { RECOVERY_UPDATE_MESSAGE, isAccountRecoveryRequest, requireAccountRecoveryCapability } = require('./accountRecoveryCapability');
const {
  resolveStoredAuthorization,
} = require("./requestAuthorization");

module.exports = {
  loginUserAlt: async (userInput, force, adminKey) => {
    const requestBody = {
      query: `
        query LoginUser($userInput: UserInput!, $force: Boolean, $adminKey: String) {
          loginUser(userInput: $userInput, force: $force, adminKey: $adminKey) {
            userId
            userEmail
            userName
            token
            reset_token
            newIPorDevice
            isVerified
            role
            tokenExpiration
          }
        }
      `,
      variables: {
        userInput: userInput,
        force: force,
        adminKey: adminKey,
      },
    };

    return requestBody;
  },

  loginUserQuery: async (userInput, baseUrl) => {
    const requestBody = {
      query: `

        query loginUser ($userInput:UserInput!){
          loginUser(userInput:$userInput)
            {userId
            userEmail
            userName
            token
            reset_token
            newIPorDevice
            isVerified
            role
            tokenExpiration}
          }

        `,
      variables: {
        userInput: userInput,
      },
    };

    return requestBody;
  },

  registerUserQuery: async (userInput, baseUrl) => {
    const requestBody = {
      query: ` 
        mutation CreateUser($input: UserInput!) { 
          createUser(input: $input) {
            email
            _id
            password
          }
        }
      `,
      variables: {
        input: userInput,
      },
    };

    return requestBody;
  },

  getUser: async (userInput, baseUrl) => {
    const requestBody = {
      query: `

      query getUser($userInput:UserInput!){
        getUser(userInput:$userInput)
          {userId
          reset_token
          userName
          role
            status}
        }

      `,
      variables: {
        userInput: userInput,
      },
    };

    return requestBody;
  },

  // Omitted purpose is the existing device/institution sign-in code check.
  // Recovery callers must choose a purpose explicitly; no grant is requested
  // or client-side account mutation performed by the sign-in query.
  verifyUserCode: async (userInput, code, baseUrl, purpose = null) => {
    if (purpose == null) return {
      query: `query verifyUserCode($userInput: UserInput!, $code: String!) {
        verifyUserCode(userInput: $userInput, code: $code) {
          userId reset_token userName role status userEmail isPendingNewUser parentUserId
        }
      }`, variables: { userInput, code },
    };
    if (!['account-verification', 'password-reset'].includes(purpose)) throw new Error(RECOVERY_UPDATE_MESSAGE);
    const requestBody = {
      query: `
      
      query verifyUserCode($userInput: UserInput!, $code: String!, $purpose: String) {
        verifyUserCode(userInput: $userInput, code: $code, purpose: $purpose)
           {userId
          reset_token
          userName
          role
          status
          userEmail
          isPendingNewUser
          recoveryGrant
          parentUserId}

        }

      `,
      variables: {
        userInput: userInput,
        code: code,
        purpose,
      },
    };
    return requestBody;
  },

  checkUser: async (userInput, baseUrl) => {
    const requestBody = {
      query: `

      query checkUser($userInput:UserInput!){
        checkUser(userInput:$userInput)
          {userId
          reset_token
          userName
          role
          userEmail
          status
          isPendingNewUser
          parentUserId
          parentEmail}
        }

      `,
      variables: {
        userInput: userInput,
      },
    };

    return requestBody;
  },

  checkUsernameAvailabilityQuery: async (username, baseUrl) => {
    const requestBody = {
      query: `

      query CheckUsernameAvailability($username: String!) {
        checkUsernameAvailability(username: $username) {
          available
          normalizedUsername
          message
          suggestions
        }
      }

      `,
      variables: {
        username,
      },
    };

    return requestBody;
  },

  updateUser: async (id, userInput, recoveryGrant) => {
    const hasGrant = typeof recoveryGrant === 'string' && /^[a-f0-9]{64}$/.test(recoveryGrant);
    if (typeof recoveryGrant === 'string' && !hasGrant) throw new Error(RECOVERY_UPDATE_MESSAGE);
    if (!hasGrant && ['password', 'isVerified'].some(field => Object.prototype.hasOwnProperty.call(userInput || {}, field))) throw new Error(RECOVERY_UPDATE_MESSAGE);
    const requestBody = {
      query: `
      mutation UpdateUser($id: ID!, $input: UserInput!${hasGrant ? ', $recoveryGrant: String' : ''}) {
        updateUser(id: $id, input: $input${hasGrant ? ', recoveryGrant: $recoveryGrant' : ''}) {
          status
        }
      }
    `,
      variables: {
        id: id,
        input: userInput,
        recoveryGrant: hasGrant ? recoveryGrant : undefined,
      },
    };
    return requestBody;
  },

  deleteUser: async (email, force, adminKey) => {
    const requestBody = {
      query: `
        mutation DeleteUser($email: String!, $force: Boolean, $adminKey: String) {
          deleteUser(email: $email, force: $force, adminKey: $adminKey) {
            status
          }
        }
      `,
      variables: {
        email: email,
        force: force,
        adminKey: adminKey,
      },
    };
    return requestBody;
  },

  updateUserAdmin: async (id, userInputAdmin, force, adminKey) => {
    const requestBody = {
      query: `
      mutation UpdateUserAdmin($id: ID!, $input: UserInputAdmin!, $force: Boolean, $adminKey: String) {
        updateUserAdmin(id: $id, input: $input, force: $force, adminKey: $adminKey) {
          status
        }
      }
    `,
      variables: {
        id: id,
        input: userInputAdmin,
        force: force,
        adminKey: adminKey,
      },
    };
    return requestBody;
  },

  checkTokenQuery: async (token, baseUrl) => {
    const requestBody = {
      query: `
          query checkToken($token: String!) {
              checkToken(checkTokenInput: { token: $token }) {
                  status
                  token
                  userId
                  username
                  email
                  isVerified
                  role
              }
          }
      `,
      variables: {
        token: token,
      },
    };

    return requestBody;
  },

  createActivityQuery: async (activityInput, baseUrl) => {
    const requestBody = {
      query: `
      mutation CreateActivity($input: ActivityInput!) {
        createActivity(input: $input) {
          loginIP
          geolocation
        }
      }
    `,
      variables: {
        input: activityInput,
      },
    };

    return requestBody;
  },

  getUserSummary: async (findUserInput, force, adminKey) => {
    const requestBody = {
      query: `

    query getUserSummary($input: findUserInput!,$force: Boolean, $adminKey: String)
      {
        getUserSummary(input: $input,force: $force, adminKey: $adminKey)
          { 

            _id
            email
            username
            phone
            isVerified
            lastVerified
            displayPicture
            userRating
            comment
            accountLoginAttempts
            temporarilyBlocked
            role
            status
            userType
            userRecordings
            userSubscriptionValidity
            isLiteUser
            liteUserPlan
            liteBillingPlan
            liteUserPlanValidity
            subscriptionBillingPeriod
            canAccessAdvanced
            canUseQHD
            canCallRestrictedMediasfuDestinations
            callTrialStartedAt
            callTrialEndsAt
            callTrialCallsUsed
            hasUsedCallTrial
            callEntitlements {
              hasCallingAccess
              subscriptionValid
              ownSipAllowed
              systemSipAllowed
              demoOnly
              activeTrial
              liteBillingPlan
              liteEntitlementPlan
              trialCallsRemaining
              trialMaxCalls
              trialMaxMinutesPerCall
              trialEndsAt
              trialDemoNumbers
              canCallRestrictedMediasfuDestinations
            }
            liteModeLockedReason
            posts{
              _id
              postNumber
            }
            postReplies{
              _id
              replyNumber
            }
            likedPosts{
              _id
              postNumber
            }
            rateLimits{
              createUser
              updateUser
              deleteUser
              createRoom
              updateRoom
              deleteRoom
              createRecording
              updateRecording
              deleteRecording
              createActivity
              updateActivity
              deleteActivity
              createAllowedDomain
              updateAllowedDomain
              deleteAllowedDomain
              createAPIKey
              updateAPIKey
              deleteAPIKey
              createBalance
              updateBalance
              deleteBalance
              createMeetingRoomParams
              updateMeetingRoomParams
              deleteMeetingRoomParams
              createRecordingSettings
              updateRecordingSettings
              deleteRecordingSettings
              createSubUser
              updateSubUser
              deleteSubUser
              createTicket
              updateTicket
              deleteTicket
              createTransaction
              updateTransaction
              deleteTransaction
              createUserSettings
              updateUserSettings
              deleteUserSettings
              createAICredentials
              updateAICredentials
              deleteAICredentials
              createSipConfigs
              updateSipConfigs
              deleteSipConfigs
              createTranslationConfigs
              updateTranslationConfigs
              deleteTranslationConfigs
            }
            rateUsage{
              createUser
              updateUser
              deleteUser
              createRoom
              updateRoom
              deleteRoom
              createRecording
              updateRecording
              deleteRecording
              createActivity
              updateActivity
              deleteActivity
              createAllowedDomain
              updateAllowedDomain
              deleteAllowedDomain
              createAPIKey
              updateAPIKey
              deleteAPIKey
              createBalance
              updateBalance
              deleteBalance
              createMeetingRoomParams
              updateMeetingRoomParams
              deleteMeetingRoomParams
              createRecordingSettings
              updateRecordingSettings
              deleteRecordingSettings
              createSubUser
              updateSubUser
              deleteSubUser
              createTicket
              updateTicket
              deleteTicket
              createTransaction
              updateTransaction
              deleteTransaction
              createUserSettings
              updateUserSettings
              deleteUserSettings
              createAICredentials
              updateAICredentials
              deleteAICredentials
              createSipConfigs
              updateSipConfigs
              deleteSipConfigs
              createTranslationConfigs
              updateTranslationConfigs
              deleteTranslationConfigs
          
            }

            userRooms {
              activeCount
              scheduledCount
              endedCount
              active_demo
              scheduled_demo
              ended_demo
              active_sandbox 
              scheduled_sandbox
              ended_sandbox
              active_production
              scheduled_production
              ended_production

            }
            userTickets {
              open
              closed
              resolved
              pending
            }
            userAction{
              create
              delete
              update
              subscribe
              unsubscribe
              other
            }
            userActivity{
              total
            }
            allowedDomains {
              statusCounts {
                active
                inactive
                pending
              }
              institutionLoginCounts {
                true
                false
              }
            }
            subUsers {
              total
              active
              inactive
              suspended
            }

            meetingRoomParams {
              _id
              webhookUrl
              webhookSecretConfigured
              backendAudioDenoise { enabled profile }
              itemPageLimit
              mediaType
              addCoHost
              targetOrientation
              targetOrientationHost
              targetResolution
              targetResolutionHost
              allowScreenSharing
              type
              audioSetting
              videoSetting
              screenshareSetting
              chatSetting
              safeRoom
              autoStartSafeRoom
              safeRoomAction
              supportSIP
              directionSIP
              supportTranslation
              translationConfigNickName
              translationOutputMode
              enableAiNotes
              aiNotesOnly
              supportMaxRoom
              supportFlexRoom
            }
            hasPersistedMeetingRoomParams
            recordingSettings {
              _id
              recordingAudioPausesLimit
              recordingAudioSupport
              recordingAudioPeopleLimit
              recordingAudioParticipantsTimeLimit
              recordingVideoPausesLimit
              recordingVideoSupport
              recordingVideoPeopleLimit
              recordingVideoParticipantsTimeLimit
              recordingAllParticipantsSupport
              recordingVideoParticipantsSupport 
              recordingAllParticipantsFullRoomSupport
              recordingVideoParticipantsFullRoomSupport
              recordingPreferredOrientation
              recordingSupportForOtherOrientation
              recordingMultiFormatsSupport
              recordingHLSSupport
              recordingAddWatermark
              recordingWatermarkPath
              recordingWatermarkPosition
            }
            refMeetingRoomParams {
              _id
              itemPageLimit
              mediaType
              addCoHost
              targetOrientation
              targetOrientationHost
              targetResolution
              targetResolutionHost
              allowScreenSharing
              type
              audioSetting
              videoSetting
              screenshareSetting
              chatSetting
              safeRoom
              autoStartSafeRoom
              safeRoomAction
              supportSIP
              directionSIP
              supportTranslation
              translationConfigNickName
              translationOutputMode
              enableAiNotes
              aiNotesOnly
              supportMaxRoom
              supportFlexRoom
            }
            refRecordingSettings {
              _id
              recordingAudioPausesLimit
              recordingAudioSupport
              recordingAudioPeopleLimit
              recordingAudioParticipantsTimeLimit
              recordingVideoPausesLimit
              recordingVideoSupport
              recordingVideoPeopleLimit
              recordingVideoParticipantsTimeLimit
              recordingAllParticipantsSupport
              recordingVideoParticipantsSupport 
              recordingAllParticipantsFullRoomSupport
              recordingVideoParticipantsFullRoomSupport
              recordingPreferredOrientation
              recordingSupportForOtherOrientation
              recordingMultiFormatsSupport
              recordingHLSSupport
              recordingAddWatermark
              recordingWatermarkPath
              recordingWatermarkPosition
            }
            userSettings {
              _id
              theme
              notifications
              preferredNotification
              defaultRecordOptions
              recordParameters
              user{
                _id
                email
                username
              }
              subuser{
                _id
                email
                username
              }
            }
            userTransactions{
              other
              credit
              debit
            }
            userBalance{

              _id

              meetingMinutesActual {
                used
                available
                total
              }
              recordingMinutesActual {
                used
                available
                total
              }
              meetingMinutesBonus {
                used
                available
                total
              }
              recordingMinutesBonus {
                used
                available
                total
              }
              meetingMinutesDemo {
                used
                available
                total
              }
              recordingMinutesDemo {
                used
                available
                total
              }
              meetingMinutesSandbox {
                used
                available
                total
              }
              recordingMinutesSandbox {
              used
                available
                total
              }

              liteCallMinutes {
                used
                total
                remaining
              }
              liteMeetings {
                used
                total
                remaining
                period
                periodResetDate
              }
              liteRecordings {
                used
                total
                remaining
                type
              }
              liteStorageGB {
                used
                total
                remaining
              }
              liteBalance {
                callMinutes {
                  used
                  total
                  remaining
                }
                meetings {
                  used
                  total
                  remaining
                  period
                  periodResetDate
                }
                recordings {
                  used
                  total
                  remaining
                  type
                }
                storageGB {
                  used
                  total
                  remaining
                }
              }

            }
            
            userAPIKeys{
              _id
              apiKey
              status
              type
              user{
                username
              }
            }

            verifiedLoginDevices
            verifiedLoginIPs
            createdAt
            telephonySupport
            updatedFor
      
          }
      }
  
    `,
      variables: {
        input: findUserInput,
        force: force,
        adminKey: adminKey,
      },
    };

    return requestBody;
  },

  userLoginRegisterRequestMulti: async (requestBody, baseUrl) => {
    try {
      const res = await fetch(baseUrl, {
        method: "POST",
        body: requestBody,
        headers: {
          // Set the Content-Type to 'multipart/form-data'
          "Content-Type": "multipart/form-data",
        },
      });

      const errorData = await res.json();

     
      try {
        if (errorData.errors && errorData.errors.length > 0) {
          const errorMessage = errorData.errors[0].message;
          return errorMessage; //'failed'
        }
      } catch (error) {}

      const resData = await errorData;

      return resData;
    } catch (err) {
      return "Sorry! something went wrong, try again.";
    }
  },

  requireAccountRecoverySupport: async (baseUrl) => {
    const headers = { 'Content-Type': 'application/json' };
    const authorization = resolveStoredAuthorization(Cookies.get);
    if (authorization) headers.Authorization = authorization;
    await requireAccountRecoveryCapability(baseUrl, fetch, headers);
  },

  userLoginRegisterRequest: async (requestBody, baseUrl, onKnownLoginOutcome = null) => {
    try {
      const headers = {
        "Content-Type": "application/json",
      };
      const authorization = resolveStoredAuthorization(Cookies.get);

      if (authorization) {
        headers.Authorization = authorization;
      }

      const recoveryRequest = isAccountRecoveryRequest(requestBody);
      if (recoveryRequest) await requireAccountRecoveryCapability(baseUrl, fetch, headers);
      if (typeof requestBody?.query === 'string' && /^\s*query RecordingsSpecific\b/.test(requestBody.query) &&
          (requestBody.query.includes(partDetailsSelection) || requestBody.query.includes(partialOutcomeSelection))) {
        requestBody = recordingRequestForCapabilities(requestBody, await readDashboardListCapabilities(baseUrl, fetch, headers));
      }

      const res = await fetch(baseUrl, {
        method: "POST",
        body: JSON.stringify(requestBody),
        headers,
      });

      let errorData = await res.json();
      if (requestBody?.variables?.userInput?.googleIdToken && typeof onKnownLoginOutcome === 'function') {
        try { onKnownLoginOutcome(); } catch (_) { /* Browser rotation is not session authority. */ }
      }
      if (res.status === 428 && errorData?.code === 'STAFF_SESSION_VERIFICATION_REQUIRED') {
        window.dispatchEvent(new Event(STAFF_VERIFICATION_EVENT));
        return 'Verify your email for this staff session.';
      }
      // A rolling fleet may route the probe and operation to different nodes.
      // A schema rejection is never retried as a weaker recovery operation.
      if (recoveryRequest && errorData?.errors?.some(error => /Unknown argument "(?:purpose|recoveryGrant)"|Cannot query field "recoveryGrant"/.test(error.message || ''))) return RECOVERY_UPDATE_MESSAGE;
      const legacyRequest = legacyRecordingPartDetailsRequest(requestBody, errorData) || legacyWebhookSummaryRequest(requestBody, errorData);
      if (legacyRequest) {
        const legacyResponse = await fetch(baseUrl, {
          method: 'POST', body: JSON.stringify(legacyRequest), headers,
        });
        errorData = await legacyResponse.json();
      }

      try {
        if (errorData.errors && errorData.errors.length > 0) {
          const errorMessage = errorData.errors[0].message;

          if (requestBody?.variables?.userInput?.googleIdToken) {
            console.log('GraphQL Error: Google sign-in could not be verified.');
            return 'Google sign-in could not be verified. Please try again.';
          }
          console.log("GraphQL Error:", errorMessage);

          if (modeDeploy == "environment") {
            return errorMessage; //'failed'
          } else {
            return "Sorry! something went wrong, try again.";
          }
        }
      } catch (error) {}

      const resData = await errorData;

      return resData;
    } catch (err) {
      if (err?.code === 'ACCOUNT_RECOVERY_SERVICE_UPDATE_REQUIRED') return RECOVERY_UPDATE_MESSAGE;
      return "Sorry! something went wrong, try again.";
    }
  },

  enableTelephonySupport: async (userId) => {
    const requestBody = {
      query: `
        mutation enableTelephonySupport($userId: ID!, $telephonyConfig: TelephonyConfigInput) {
          enableTelephonySupport(userId: $userId, telephonyConfig: $telephonyConfig) {
            success
            message
            user {
              _id
              telephonySupport
            }
          }
        }
      `,
      variables: {
        userId: userId,
        telephonyConfig: {}, // Use backend defaults
      },
    };

    return requestBody;
  },

  disableTelephonySupport: async (userId, forceDisable = false) => {
    const requestBody = {
      query: `
        mutation disableTelephonySupport($userId: ID!, $forceDisable: Boolean) {
          disableTelephonySupport(userId: $userId, forceDisable: $forceDisable) {
            success
            message
            user {
              _id
              telephonySupport
            }
          }
        }
      `,
      variables: {
        userId: userId,
        forceDisable: forceDisable,
      },
    };

    return requestBody;
  },

  /// Get email for social provider (Apple, Google, GitHub)
  /// Used when Apple only provides userIdentifier on subsequent logins
  /// Backend stores the mapping automatically during loginUser with socialProvider fields
  getSocialProviderEmail: async (provider, providerId) => {
    const requestBody = {
      query: `
        query GetSocialProviderEmail($provider: String!, $providerId: String!) {
          getSocialProviderEmail(provider: $provider, providerId: $providerId) {
            success
            email
            message
          }
        }
      `,
      variables: {
        provider: provider,
        providerId: providerId,
      },
    };

    return requestBody;
  },
};
